// Purchase orders in Inventory (inventory prototype; gap #10): raise one to a vendor for the
// warehouse or a truck (purchaseOrders.create), change a draft's lines (purchaseOrders.updateLines),
// send it (purchaseOrders.send), receive what came (purchaseOrders.receive) and cancel it or what's
// left of it, with the reason (purchaseOrders.cancel: a sent PO's vendor is emailed), over
// purchaseOrders.list and purchaseOrders.get. The owner approves one over the limit in Payables.
// A PO can be for one job's parts (F-24): picked from the open jobs (jobs.list), named on its row and
// sheet, and the sheet opens the job.
// What came can be sent back to the vendor from the PO's sheet (Send some back: a return, Returns.tsx).
// A refusal says the server's words, under the button that was pressed.

import { useEffect, useRef, useState } from 'react';
import { client } from '../api.ts';
import { Empty, Loading, Money, Panel, errorText, useQuery } from '../books/ui.tsx';
import { newId } from '../shared/ids.ts';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { openJob } from '../job/job-page.ts';
import { checkUntilSettled } from './po-sending.ts';
import { boxText, qtyText, unitPlural, type StockLocation } from './model.ts';
import {
  editable,
  jobLabel,
  lineProblems,
  OPEN_JOB_STATUSES,
  linesInput,
  linesOf,
  openQty,
  PO_FILTERS,
  PO_STATUS,
  notSentText,
  stOwned,
  poNote,
  receivable,
  receiveForm,
  receiveLines,
  receiveProblems,
  receivedText,
  type LineForm,
  type OpenJob,
  type Po,
  type ReceiveForm,
  type Vendor,
  cancellable,
  cancelledNote,
  cancelOutcome,
} from './orders-model.ts';
import '../review/review.css';
import './inventory.css';

interface Item {
  id: string;
  name: string;
  code: string;
  type: string;
  costCents?: number | null;
}

/** When a PO went, on this device's calendar: "Oct 15". */
const sentText = (iso: string) =>
  new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric' }).format(new Date(iso));

/** A write with its own busy and why-it-failed, for one button (L-action). */
function useWrite() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async <T,>(f: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setError(null);
    try {
      return await f();
    } catch (e) {
      setError(errorText(e));
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run, clear: () => setError(null) };
}

export function Orders({
  today,
  toast,
  mayOrder,
  mayReceive,
  locations,
  onReturn,
}: {
  today: string;
  toast: (m: string) => void;
  mayOrder: boolean;
  mayReceive: boolean;
  locations: StockLocation[];
  /** Send some of a PO's received parts back to its vendor (BL-013). */
  onReturn?: (po: Po) => void;
}) {
  const [status, setStatus] = useState<string | null>(null);
  const q = useQuery<{ orders: Po[] }>('purchaseOrders.list', status ? { status } : {});
  const vendors = useQuery<{ vendors: Vendor[] }>('vendors.list', {});
  const [openId, setOpenId] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const vendorName = (id: string) => vendors.data?.vendors.find((v) => v.id === id)?.name ?? 'A vendor';
  const placeName = (id: string) => locations.find((l) => l.id === id)?.name ?? 'Another location';
  const changed = async (said: string, poId?: string) => {
    toast(said);
    await q.reload();
    if (poId) setOpenId(poId);
  };
  // a PO waiting on the mail provider: read the list again until it has gone (QA-861)
  const sending = q.data?.orders.some((o) => o.status === 'sending') ?? false;
  const reloadList = q.reload;
  useEffect(() => {
    if (!sending) return;
    return checkUntilSettled(
      async () =>
        !(await client.call<{ orders: Po[] }>('purchaseOrders.list', status ? { status } : {})).orders.some(
          (o) => o.status === 'sending',
        ),
      () => void reloadList(),
    );
  }, [sending, reloadList, status]);
  return (
    <>
      <Panel
        title="Orders"
        aside={
          <div className="row-actions">
            <div className="pills" role="group" aria-label="Which orders">
              {PO_FILTERS.map((f) => (
                <button
                  key={f.label}
                  type="button"
                  className="pill"
                  aria-pressed={status === f.status}
                  onClick={() => setStatus(f.status)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {mayOrder && !making && (
              <button type="button" className="btn" onClick={() => setMaking(true)}>
                New purchase order
              </button>
            )}
          </div>
        }
      >
        {!q.data ? (
          <Loading
            error={q.error}
            what="the purchase orders"
            kind="table"
            cols={['PO', 'Vendor', 'Deliver to', 'Total', 'Status']}
            retry={() => void q.reload()}
          />
        ) : q.data.orders.length === 0 ? (
          <Empty>
            {status
              ? 'No purchase orders here.'
              : `No purchase orders yet.${mayOrder ? ' Raise one, or let the weekly reorder draft them from each item’s minimum.' : ''}`}
          </Empty>
        ) : (
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table" aria-label="Purchase orders">
              <thead>
                <tr>
                  <th>PO</th>
                  <th>Vendor</th>
                  <th>Deliver to</th>
                  <th className="num">Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {q.data.orders.map((p) => (
                  <tr key={p.id} className={openId === p.id ? 'on' : undefined}>
                    <td>
                      <button
                        type="button"
                        className="row-btn"
                        aria-expanded={openId === p.id}
                        onClick={() => setOpenId(openId === p.id ? null : p.id)}
                      >
                        <b>{p.number}</b>
                        {p.auto && <span className="muted small">weekly reorder</span>}
                        {p.jobNumber && <span className="muted small">for job {p.jobNumber}</span>}
                      </button>
                    </td>
                    <td>{vendorName(p.vendorId)}</td>
                    <td>{placeName(p.locationId)}</td>
                    <td className="num">
                      <Money cents={p.totalCents} />
                    </td>
                    <td>
                      <span
                        className={`chip say${p.status === 'received' ? ' ok' : p.needsOwnerApproval && p.status === 'draft' ? ' warn' : ''}`}
                      >
                        {PO_STATUS[p.status] ?? p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {making && (
        <Panel
          title="A new purchase order"
          aside={
            <button type="button" className="btn quiet" onClick={() => setMaking(false)}>
              Cancel
            </button>
          }
        >
          <NewOrder
            vendors={(vendors.data?.vendors ?? []).filter((v) => v.active)}
            locations={locations}
            onMade={(p) => {
              setMaking(false);
              void changed(
                `${p.number} drafted for ${vendorName(p.vendorId)}. Send it when it’s right.`,
                p.id,
              );
            }}
          />
        </Panel>
      )}

      {openId && (
        <OneOrder
          key={openId}
          id={openId}
          today={today}
          vendorName={vendorName}
          placeName={placeName}
          locations={locations}
          mayOrder={mayOrder}
          mayReceive={mayReceive}
          {...(onReturn ? { onReturn } : {})}
          onChanged={(said) => void changed(said, openId)}
          onSettled={() => void q.reload()}
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  );
}

// ── the lines editor (create and a draft's lines) ──

export function ItemPicker({ onPick, taken }: { onPick: (i: Item) => void; taken: readonly string[] }) {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<Item[] | null>(null);
  useEffect(() => {
    if (!query.trim()) return setFound(null);
    const t = setTimeout(() => {
      // Only materials and equipment are ordered; services never are.
      void Promise.all(
        (['material', 'equipment'] as const).map((type) =>
          client
            .call<{ items: Item[] }>('pricebook.listItems', {
              query: query.trim(),
              type,
              // a part kept in stock only is bought too (M9)
              includeNotForSale: true,
              limit: 8,
            })
            .then((r) => r.items),
        ),
      )
        .then((rs) => setFound(rs.flat().sort((a, b) => a.name.localeCompare(b.name))))
        .catch(() => setFound([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);
  const list = (found ?? []).filter((i) => !taken.includes(i.id));
  return (
    <div className="po-pick full">
      <label>
        <span className="flabel">Add an item from the price book</span>
        <input
          className="field"
          type="search"
          placeholder="Name or code"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {found && (
        <ul className="found" aria-label="Price book items">
          {list.length ? (
            list.map((i) => (
              <li key={i.id}>
                <button
                  type="button"
                  className="row-btn"
                  onClick={() => {
                    onPick(i);
                    setQuery('');
                  }}
                >
                  <span className="who-col">
                    <b>{i.name}</b> <span className="muted small">{i.code}</span>
                  </span>
                  {i.costCents != null && <Money cents={i.costCents} />}
                </button>
              </li>
            ))
          ) : (
            <li className="muted small">No material or equipment matches “{query.trim()}”.</li>
          )}
        </ul>
      )}
    </div>
  );
}

function LinesEditor({ lines, onChange }: { lines: LineForm[]; onChange: (l: LineForm[]) => void }) {
  const set = (i: number, patch: Partial<LineForm>) =>
    onChange(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  return (
    <>
      {lines.length > 0 && (
        <ul className="po-lines full">
          {lines.map((l, i) => (
            <li key={l.itemId} className="po-line">
              <b className="po-line-name">{l.name}</b>
              <label>
                <span className="flabel">Quantity</span>
                <input
                  className="field"
                  inputMode="decimal"
                  value={l.quantity}
                  onChange={(e) => set(i, { quantity: e.target.value })}
                />
              </label>
              <label>
                <span className="flabel">Unit cost</span>
                <input
                  className="field"
                  inputMode="decimal"
                  placeholder="Book cost"
                  value={l.cost}
                  onChange={(e) => set(i, { cost: e.target.value })}
                />
              </label>
              <button
                type="button"
                className="btn quiet"
                aria-label={`Remove ${l.name}`}
                onClick={() => onChange(lines.filter((_, k) => k !== i))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <ItemPicker
        taken={lines.map((l) => l.itemId)}
        onPick={(i) =>
          onChange([
            ...lines,
            {
              itemId: i.id,
              name: i.name,
              quantity: '1',
              cost: i.costCents != null ? (i.costCents / 100).toFixed(2) : '',
            },
          ])
        }
      />
    </>
  );
}

function NewOrder({
  vendors,
  locations,
  onMade,
}: {
  vendors: Vendor[];
  locations: StockLocation[];
  onMade: (p: Po) => void;
}) {
  const [vendorId, setVendorId] = useState('');
  const [locationId, setLocationId] = useState(locations.find((l) => l.kind === 'warehouse')?.id ?? '');
  const [lines, setLines] = useState<LineForm[]>([]);
  const [note, setNote] = useState('');
  const [jobId, setJobId] = useState('');
  const jobs = useQuery<{ jobs: OpenJob[] }>('jobs.list', { status: [...OPEN_JOB_STATUSES], limit: 100 });
  const [tried, setTried] = useState(false);
  const w = useWrite();
  const save = useSaveKey(JSON.stringify({ vendorId, locationId, jobId, lines, note }));
  const problems = [
    ...(!vendorId ? ['Pick the vendor.'] : []),
    ...(!locationId ? ['Pick where it goes.'] : []),
    ...lineProblems(lines),
  ];
  const make = async () => {
    setTried(true);
    if (problems.length) return;
    const p = await w.run(() =>
      client.call<Po>(
        'purchaseOrders.create',
        {
          vendorId,
          locationId,
          ...(jobId ? { jobId } : {}),
          lines: linesInput(lines),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
        { idempotencyKey: save.key },
      ),
    );
    if (p) onMade(p);
  };
  return (
    <form
      className="fgrid"
      onSubmit={(e) => {
        e.preventDefault();
        if (!w.busy) void make();
      }}
    >
      <label>
        <span className="flabel">Vendor</span>
        <select className="field" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
          <option value="">Pick a vendor…</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
              {v.email ? '' : ' (no email yet)'}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="flabel">Deliver to</span>
        <select className="field" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </label>
      <label className="full">
        <span className="flabel">For a job (optional)</span>
        <select
          className="field"
          value={jobId}
          disabled={!jobs.data}
          onChange={(e) => setJobId(e.target.value)}
        >
          <option value="">{jobs.data ? 'Stock, not one job' : 'Loading the open jobs…'}</option>
          {(jobs.data?.jobs ?? []).map((j) => (
            <option key={j.id} value={j.id}>
              {jobLabel(j)}
            </option>
          ))}
        </select>
        <span className="muted small block">
          The parts for one job: the job shows them on order, and when they’re in, it’s time to book the
          return.
        </span>
      </label>
      <LinesEditor lines={lines} onChange={setLines} />
      <label className="full">
        <span className="flabel">Note for the vendor (optional)</span>
        <input className="field" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {tried && problems.length > 0 && (
        <div className="notice warn full" role="alert">
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="row-actions full">
        <SaveButton busy={w.busy} busyLabel="Drafting…" error={w.error} onClick={() => void make()}>
          Draft the purchase order
        </SaveButton>
      </div>
      <p className="muted small full">Nothing goes to the vendor until you send it.</p>
    </form>
  );
}

// ── one PO ──

function OneOrder({
  id,
  today,
  vendorName,
  placeName,
  locations,
  mayOrder,
  mayReceive,
  onReturn,
  onChanged,
  onSettled,
  onClose,
}: {
  id: string;
  today: string;
  vendorName: (id: string) => string;
  placeName: (id: string) => string;
  locations: StockLocation[];
  mayOrder: boolean;
  mayReceive: boolean;
  onReturn?: (po: Po) => void;
  onChanged: (said: string) => void;
  /** The PO went from sending to sent while open: the list reads it again too. */
  onSettled: () => void;
  onClose: () => void;
}) {
  const q = useQuery<Po>('purchaseOrders.get', { id });
  // waiting on the mail provider: check back until it has gone, then show it as sent (QA-861)
  const waiting = q.data?.status === 'sending';
  const reloadPo = q.reload;
  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;
  useEffect(() => {
    if (!waiting) return;
    return checkUntilSettled(
      async () => (await client.call<Po>('purchaseOrders.get', { id })).status !== 'sending',
      () => {
        void reloadPo();
        settledRef.current();
      },
    );
  }, [waiting, id, reloadPo]);
  const mirroring = useMirror();
  const [editing, setEditing] = useState<LineForm[] | null>(null);
  const [receiving, setReceiving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const send = useWrite();
  const saveLines = useWrite();
  const sendKey = useSaveKey(`send:${id}`);
  const linesKey = useSaveKey(`lines:${id}:${JSON.stringify(editing)}`);
  const p = q.data;
  const done = async (said: string) => {
    await q.reload();
    onChanged(said);
  };
  return (
    <Panel
      title={p ? `${p.number} · ${vendorName(p.vendorId)}` : 'Purchase order'}
      aside={
        <button type="button" className="btn quiet" onClick={onClose}>
          Close
        </button>
      }
    >
      {!p ? (
        <Loading error={q.error} what="the purchase order" retry={() => void q.reload()} />
      ) : (
        <div className="po-detail">
          <div className="row">
            <span className={`chip${p.status === 'received' ? ' ok' : ''}`}>
              {PO_STATUS[p.status] ?? p.status}
            </span>
            <span className="muted small">
              To {placeName(p.locationId)}
              {p.sentAt ? ` · sent ${sentText(p.sentAt)}` : ''}
            </span>
            {p.jobId && (
              <button type="button" className="btn quiet sm" onClick={() => openJob(p.jobId!)}>
                {p.jobNumber ? `Open job ${p.jobNumber}` : 'Open the job'}
              </button>
            )}
          </div>
          {/* a send that didn't go (QA-566), beside the office's urgent notice; then the office's note */}
          {notSentText(p) && (
            <p className="notice warn" role="status">
              {notSentText(p)}
            </p>
          )}
          {p.note && <p className="small muted">Note: {p.note}</p>}
          {poNote(p) && <p className={`notice${p.needsOwnerApproval ? ' warn' : ''}`}>{poNote(p)}</p>}
          {cancelledNote(p, sentText) && (
            <p className={`notice${p.cancelled?.vendorTellFailure ? ' warn' : ''}`}>
              {cancelledNote(p, sentText)}
            </p>
          )}

          {editing ? (
            <div className="fgrid">
              <LinesEditor lines={editing} onChange={setEditing} />
              {lineProblems(editing).length > 0 && (
                <div className="notice warn full" role="alert">
                  <ul>
                    {lineProblems(editing).map((x) => (
                      <li key={x}>{x}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="row-actions full">
                <button type="button" className="btn quiet" onClick={() => setEditing(null)}>
                  Keep it as it was
                </button>
                <SaveButton
                  busy={saveLines.busy}
                  busyLabel="Saving…"
                  error={saveLines.error}
                  disabled={lineProblems(editing).length > 0}
                  onClick={() =>
                    void saveLines
                      .run(() =>
                        client.call<Po>(
                          'purchaseOrders.updateLines',
                          { poId: p.id, lines: linesInput(editing) },
                          { idempotencyKey: linesKey.key },
                        ),
                      )
                      .then((r) => {
                        if (!r) return;
                        setEditing(null);
                        void done(`${p.number}’s lines saved.`);
                      })
                  }
                >
                  Save the lines
                </SaveButton>
              </div>
            </div>
          ) : (
            <div className="table-wrap" ref={scrollFocus}>
              <table className="grid-table" aria-label={`${p.number} lines`}>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">Ordered</th>
                    <th className="num">Unit cost</th>
                    <th className="num">Received</th>
                    <th>Still expected</th>
                  </tr>
                </thead>
                <tbody>
                  {p.lines.map((l) => (
                    <tr key={l.id}>
                      <td>{l.description}</td>
                      <td className="num">
                        {boxText(l.quantity, l.unitsPerPurchase ?? '1', l.purchaseUnit ?? null)}
                      </td>
                      <td className="num">
                        <Money cents={l.unitCostCents} />
                      </td>
                      <td className="num">{qtyText(l.receivedQty)}</td>
                      <td>
                        {openQty(l) > 0
                          ? `${qtyText(openQty(l))}${l.backOrdered ? ' · back-ordered' : ''}`
                          : Number(l.cancelledQty) > 0
                            ? `None · ${qtyText(l.cancelledQty)} cancelled`
                            : 'None'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>
                      <b>Total</b>
                    </td>
                    <td className="num">
                      <b>
                        <Money cents={p.totalCents} />
                      </b>
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {!editing && stOwned(p, mirroring) && <p className="muted small">{MIRROR_LINE}</p>}
          {!editing && !stOwned(p, mirroring) && (
            <div className="row-actions">
              {editable(p) && mayOrder && (
                <button type="button" className="btn" onClick={() => setEditing(linesOf(p))}>
                  Change the lines
                </button>
              )}
              {editable(p) && mayOrder && (
                <SaveButton
                  busy={send.busy}
                  busyLabel="Sending…"
                  error={send.error}
                  onClick={() =>
                    void send
                      .run(() =>
                        client.call<Po>(
                          'purchaseOrders.send',
                          { poId: p.id },
                          { idempotencyKey: sendKey.key },
                        ),
                      )
                      .then((r) => {
                        if (r) void done(`${p.number} is on its way to ${vendorName(p.vendorId)}.`);
                      })
                  }
                >
                  Send to {vendorName(p.vendorId)}
                </SaveButton>
              )}
              {receivable(p) && mayReceive && (
                <button type="button" className="btn primary" onClick={() => setReceiving(true)}>
                  Receive
                </button>
              )}
              {onReturn && mayReceive && p.lines.some((l) => Number(l.receivedQty) > 0) && (
                <button type="button" className="btn" onClick={() => onReturn(p)}>
                  Send some back
                </button>
              )}
              {cancellable(p) && mayOrder && (
                <button type="button" className="btn cancel-po" onClick={() => setCancelling(true)}>
                  {p.status === 'partially_received' ? 'Cancel the rest' : 'Cancel this order'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {p && cancelling && (
        <CancelOrder
          po={p}
          vendor={vendorName(p.vendorId)}
          onClose={() => setCancelling(false)}
          onCancelled={(said) => {
            setCancelling(false);
            void done(said);
          }}
        />
      )}
      {p && receiving && (
        <ReceiveDialog
          po={p}
          today={today}
          locations={locations}
          onClose={() => setReceiving(false)}
          onReceived={(said) => {
            setReceiving(false);
            void done(said);
          }}
        />
      )}
    </Panel>
  );
}

function CancelOrder({
  po,
  vendor,
  onClose,
  onCancelled,
}: {
  po: Po;
  vendor: string;
  onClose: () => void;
  onCancelled: (said: string) => void;
}) {
  const [reason, setReason] = useState('');
  const w = useWrite();
  const partial = po.status === 'partially_received';
  const cancel = async () => {
    const r = await w.run(() =>
      client.call<Po>('purchaseOrders.cancel', { poId: po.id, reason: reason.trim() }),
    );
    if (r)
      onCancelled(
        partial
          ? `The rest of ${po.number} is cancelled.${po.sentAt ? ` ${vendor} is being emailed.` : ''}`
          : `${po.number} is cancelled.${po.sentAt ? ` ${vendor} is being emailed.` : ''}`,
      );
  };
  return (
    <MoneySheet
      title={partial ? `Cancel the rest of ${po.number}?` : `Cancel ${po.number}?`}
      outcome={cancelOutcome(po, vendor)}
      confirm={partial ? 'Cancel the rest' : 'Cancel the order'}
      ready={!!reason.trim()}
      busy={w.busy}
      error={w.error}
      onConfirm={() => void cancel()}
      onClose={onClose}
    >
      <label>
        Why (required)
        <input
          className="field"
          maxLength={500}
          value={reason}
          placeholder="The job was cancelled; ordered twice; the vendor is out of stock"
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
    </MoneySheet>
  );
}

function ReceiveDialog({
  po,
  today,
  locations,
  onClose,
  onReceived,
}: {
  po: Po;
  today: string;
  locations: StockLocation[];
  onClose: () => void;
  onReceived: (said: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [lines, setLines] = useState<ReceiveForm[]>(() => receiveForm(po));
  const [receivedOn, setReceivedOn] = useState(today);
  const [locationId, setLocationId] = useState(po.locationId);
  const [note, setNote] = useState('');
  // One receipt per attempt: a lost answer retried records it once (the action replays by id).
  const [receiptId] = useState(newId);
  const w = useWrite();
  const problems = receiveProblems(lines);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const set = (i: number, patch: Partial<ReceiveForm>) =>
    setLines((ls) => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const receive = async () => {
    if (problems.length) return;
    const r = await w.run(() =>
      client.call<{ valueCents: number; po: Po }>('purchaseOrders.receive', {
        receiptId,
        poId: po.id,
        receivedOn,
        ...(locationId !== po.locationId ? { locationId } : {}),
        lines: receiveLines(lines),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    );
    if (r) onReceived(receivedText(po.number, lines, r.po.status));
  };
  return (
    <dialog ref={ref} className="sheet ask receive-dialog" aria-labelledby="receive-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (!w.busy) void receive();
        }}
      >
        <h3 id="receive-title">Receive {po.number}</h3>
        <p className="muted">What came, line by line. It goes into stock at the PO’s cost.</p>
        <ul className="recv-lines">
          {lines.map((l, i) => {
            const short = Number(l.got) < l.open;
            return (
              <li key={l.poLineId}>
                <b>{l.name}</b>
                <span className="muted small">
                  {boxText(String(l.open), l.per ?? '1', l.unit ?? null)} expected
                </span>
                <label>
                  {l.unit ? `Arrived (${unitPlural(l.unit)})` : 'Arrived'}
                  <input
                    className="field"
                    inputMode="decimal"
                    value={l.got}
                    onChange={(e) => set(i, { got: e.target.value })}
                  />
                </label>
                {short && (
                  <label>
                    The rest
                    <select
                      className="field"
                      value={l.rest}
                      onChange={(e) => set(i, { rest: e.target.value as ReceiveForm['rest'] })}
                    >
                      <option value="back_order">Back-ordered: still coming</option>
                      <option value="cancel">Cancelled: expect a credit note</option>
                    </select>
                  </label>
                )}
              </li>
            );
          })}
        </ul>
        <div className="fgrid">
          <label>
            Received on
            <input
              className="field"
              type="date"
              value={receivedOn}
              max={today}
              onChange={(e) => setReceivedOn(e.target.value)}
            />
          </label>
          {locations.length > 1 && (
            <label>
              Into
              <select className="field" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <label>
          Note (optional)
          <input className="field" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        {problems.length > 0 && (
          <div className="notice warn" role="alert">
            <ul>
              {problems.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <SaveButton
            busy={w.busy}
            busyLabel="Receiving…"
            error={w.error}
            disabled={problems.length > 0}
            onClick={() => void receive()}
          >
            Receive it
          </SaveButton>
        </div>
      </form>
    </dialog>
  );
}
